import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components/macro'
import OllieAskLink from 'src/components/ollie/OllieAskLink'
import useOllieAtLimit from 'src/hooks/useOllieAtLimit'
import useOllieVenueId from 'src/hooks/useOllieVenueId'
import { isOllieDraftAskBlocked } from 'src/utils/ollieAskLimit'
import {
  trackOllieContextualAskOpened,
  trackOllieDiscoverabilityExposed,
} from 'src/utils/ollieDiscoverability'
import { trackOllieSuggestionSelected } from 'src/utils/ollieSuggestions'
import { venueHomeOlliePrompts } from 'src/utils/venueHomeApi'

/**
 * Quiet task examples on Home — replaces a generic "Ask Ollie" invite with
 * prompts this role can actually use. Selecting one opens Ollie with that
 * question in the composer (unsent). Gated by discoverability rollout.
 */
const VenueHomeAskOllie = ({ canAct, role }) => {
  const atLimit = useOllieAtLimit()
  const { venueId } = useOllieVenueId()
  const discoverability = useSelector(
    state => state.ollie?.discoverabilityEntries,
  )
  const cohort = discoverability?.cohort || 'all'
  const entriesEnabled = discoverability?.enabled !== false

  const prompts =
    entriesEnabled && !isOllieDraftAskBlocked({ atLimit })
      ? venueHomeOlliePrompts(canAct, role)
      : []

  useEffect(() => {
    if (!prompts.length || !venueId) return
    trackOllieDiscoverabilityExposed({
      venueId,
      entryPoint: 'home',
      page: 'home',
      cohort,
    })
  }, [prompts.length, venueId, cohort])

  if (!prompts.length) return null

  return (
    <Wrap data-testid='venue-home-ask-ollie'>
      {prompts.map(prompt => (
        <OllieAskLink
          key={prompt.id}
          question={prompt.text}
          label={prompt.text}
          quiet
          data-testid={`venue-home-ask-ollie-${prompt.id}`}
          onOpen={() => {
            trackOllieContextualAskOpened({
              venueId,
              entryPoint: 'home',
              page: 'home',
              question: prompt.text,
              cohort,
            })
            trackOllieSuggestionSelected({
              venueId,
              promptId: prompt.id,
              question: prompt.text,
              surface: 'home',
              page: 'home',
            })
          }}
        />
      ))}
    </Wrap>
  )
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.2rem;
  margin: 0.55rem 0 0;

  button {
    margin: 0;
  }
`

export default VenueHomeAskOllie
